import { LoadingOutlined } from '@ant-design/icons';
import { Button, List, Modal, Spin, Table } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  downloadCSVAction,
  getSyncAppointmentHistory,
  reProcessUpload,
} from 'src/store/actions/appointment.action';
import { csvDownloadUrlSlice } from 'src/store/slices/appointments.slice';
import { showToastSuccess } from 'src/utilities/toast';
import '../styles/AppointmentsCSVUploaderHistory.scss';

const AppointmentsCSVUploaderHistory = ({ visible, setVisible }) => {
  const dispatch = useDispatch();
  const {
    bulkAppointmentUploadHistory,
    bulkAppointmentUploadHistoryLoading,
    csvDownloadUrl,
  } = useSelector((state) => state?.appointmentState);
  const [pagination, setPagination] = useState({
    current: 1,
    per_page: 4,
  });
  const [s3Key, setS3Key] = useState('');
  const [bucketName, setBucketName] = useState(null);

  const [detailsVisible, setDetailsVisible] = useState(false);
  const [verboseErrors, setVerboseErrors] = useState({});
  const [downloadVisible, setDownloadVisible] = useState(false);
  const [reProcessVisible, setReProcessVisible] = useState(false);
  const [_reProcessLoading, setReProcessLoading] = useState(false);
  const [_processingRowId, setProcessingRowId] = useState(null);

  useEffect(() => {
    dispatch(
      getSyncAppointmentHistory(pagination.current, pagination.per_page)
    );
  }, [dispatch, pagination]);

  const _formatDownloadFileName = (s3_key) => {
    const fileName = s3_key.split('/');
    return fileName[fileName.length - 1];
  };

  useEffect(() => {
    if (csvDownloadUrl && s3Key) {
      const link = document.createElement('a');
      link.href = csvDownloadUrl;
      link.download = s3Key.split('/').pop();
      document.body.appendChild(link);
      link.click();
      link.remove();
      setS3Key('');
      setBucketName(null);
      dispatch(csvDownloadUrlSlice(null));
    }
  }, [csvDownloadUrl, s3Key, dispatch]);

  const handleClose = () => {
    setVisible(false);
  };

  const handleDetailsClose = () => {
    setDetailsVisible(false);
  };

  const handleDownloadClose = () => {
    setDownloadVisible(false);
  };

  const handleReProcessClose = () => {
    setReProcessVisible(false);
  };

  const downloadFileHandler = async () => {
    setDownloadVisible(false);
    try {
      showToastSuccess('Downloading file...');
      const payload = { s3_key: s3Key };
      if (bucketName) {
        payload.bucket_name = bucketName;
      }

      dispatch(downloadCSVAction(payload));
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const handlePageChange = (page, pageSize) => {
    setPagination({
      ...pagination,
      current: page,
      per_page: pageSize,
    });
  };

  const handleViewDetails = (record) => {
    setVerboseErrors(record.verbose_errors);
    setDetailsVisible(true);
  };

  const _handleReProcess = (record) => {
    setProcessingRowId(record?.history_id);
    setS3Key(record?.s3_key);
    setBucketName(record?.bucket_name);
    setReProcessVisible(true);
  };

  const reProcessHandler = () => {
    setReProcessVisible(false);
    setReProcessLoading(true);
    dispatch(reProcessUpload(s3Key, bucketName)).then((result) => {
      setReProcessLoading(false);
      setProcessingRowId(null);
      if (result) {
        dispatch(getSyncAppointmentHistory(1, 4));
      }
    });
  };

  const columns = [
    {
      title: 'Upload date',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text, record) => (
        // show moment.utc({record.created_at}).local().format('MMM D, YYYY h:mm A') with an hyperlink to the s3_key if exists, else just show plain moment.utc({record.created_at}).local().format('MMM D, YYYY h:mm A')
        <>
          {record.s3_key && record.s3_key !== '' ? (
            <Link
              to="#"
              onClick={() => {
                setS3Key(record.s3_key);
                setBucketName(record?.bucket_name);
                setDownloadVisible(true);
              }}
              className="hyperlink-style"
            >
              {moment
                .utc(record.created_at)
                .local()
                .format('MMM D, YYYY h:mm A')}
            </Link>
          ) : (
            moment.utc(record.created_at).local().format('MMM D, YYYY h:mm A')
          )}
        </>
      ),
    },
    {
      title: 'Uploaded by',
      dataIndex: 'created_by',
      key: 'created_by',
      render: (text, record) => {
        return record.created_by === 'api@insighthealth.ai'
          ? 'SFTP Upload'
          : record.created_by;
      },
    },
    {
      title: 'Upload status',
      key: 'status',
      render: (text, record) => (
        <div className="flex flex-col">
          <div>
            {record.status === 'completed' ? (
              <div>
                <div className="text-green-500">Created: {record.created}</div>
                <div className="text-orange-500">Updated: {record.updated}</div>
                <div className="text-red-500">Skipped: {record.skipped}</div>
                <div className="text-gray-500">
                  Unchanged: {record.unchanged}
                </div>
                <div className="text-green-800">
                  Aura Only: {record.intake_skipped}
                </div>
                {record.verbose_errors != null && record.skipped > 0 && (
                  <>
                    <Button
                      className="details-button"
                      size="small"
                      type="primary"
                      onClick={() => handleViewDetails(record)}
                    >
                      View details
                    </Button>
                  </>
                )}
              </div>
            ) : record.status === 'in_progress' ? (
              <Spin indicator={<LoadingOutlined spin />} size="large" />
            ) : record.status === 'failed' ? (
              <div className="text-red-500">Failed</div>
            ) : (
              <div className="text-gray-500">Unknown</div>
            )}
            {/* <div className="flex items-center mt-2">
              {record?.s3_key &&
                record?.bucket_name &&
                record?.status !== 'in_progress' && (
                  <Button
                    className="re-process-button"
                    size="small"
                    type="primary"
                    onClick={() => _handleReProcess(record)}
                    loading={
                      _reProcessLoading && _processingRowId === record.history_id
                    }
                  >
                    Re-process
                  </Button>
                )}
            </div> */}
          </div>
        </div>
      ),
    },
  ];

  return (
    <div className="csv-uploader-history">
      <Modal
        open={visible}
        onCancel={handleClose}
        footer={null}
        centered={true}
        width="60vw"
        title="Bulk upload history"
        className="csv-uploader-history-modal"
      >
        <Table
          loading={bulkAppointmentUploadHistoryLoading}
          dataSource={bulkAppointmentUploadHistory?.histories}
          columns={columns}
          rowKey={(record) => record.created}
          pagination={{
            total: bulkAppointmentUploadHistory?.total,
            pageSize: pagination.per_page,
            onChange: handlePageChange,
            pageSizeOptions: [4, 10, 20],
          }}
          className="insight-background p-4"
        />
      </Modal>

      <Modal
        open={detailsVisible}
        onCancel={handleDetailsClose}
        footer={null}
        centered={true}
        width="50vw"
        title="Details"
        className="csv-uploader-history-modal"
        styles={{ body: { height: '600px', overflow: 'auto' } }}
      >
        <List
          size="small"
          dataSource={verboseErrors}
          renderItem={(item) => (
            <List.Item className="text-sm">{item}</List.Item>
          )}
        />
      </Modal>

      <Modal
        open={downloadVisible}
        closable={false}
        centered={true}
        styles={{ body: { height: '35px' } }}
        footer={[
          <div className="flex justify-end gap-1" key="action-buttons">
            <button
              key="back"
              className="bg-[#A9A9A9] hover:bg-[#899499] text-white font-normal p-1 py-1 px-2 rounded-md focus:outline-none focus:shadow-outline"
              onClick={handleDownloadClose}
            >
              Cancel
            </button>
            <button
              key="submit"
              className="bg-[#00D090] hover:bg-[#059669] text-white font-normal p-1 py-1 px-2 rounded-md focus:outline-none focus:shadow-outline"
              onClick={downloadFileHandler}
            >
              Download
            </button>
          </div>,
        ]}
      >
        <div>
          <p>Are you sure you want to download this file?</p>
        </div>
      </Modal>

      <Modal
        open={reProcessVisible}
        closable={false}
        centered={true}
        styles={{ body: { height: '35px' } }}
        footer={[
          <div className="flex justify-end gap-1" key="action-buttons">
            <button
              key="back"
              className="bg-[#A9A9A9] hover:bg-[#899499] text-white font-normal p-1 py-1 px-2 rounded-md focus:outline-none focus:shadow-outline"
              onClick={handleReProcessClose}
            >
              Cancel
            </button>
            <button
              key="submit"
              className="bg-[#00D090] hover:bg-[#059669] text-white font-normal p-1 py-1 px-2 rounded-md focus:outline-none focus:shadow-outline"
              onClick={reProcessHandler}
            >
              Submit
            </button>
          </div>,
        ]}
      >
        <div>
          <p>Are you sure you want to re-process this upload?</p>
        </div>
      </Modal>
    </div>
  );
};

export default AppointmentsCSVUploaderHistory;
